import {Injectable} from '@angular/core';
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  QuerySnapshot,
  runTransaction,
  setDoc,
  updateDoc
} from '@angular/fire/firestore';
import {IStore} from '../../shared-models/store/store';
import {
  IAdvancedFilterGroup
} from '../../../features/settings-new/models/general/advanced-filter-group.interface';
import {
  ADVANCED_FILTER_GRID_STOCK_TAKE,
  isGeneralAdvancedFilterGrid,
} from '../../../features/settings-new/models/general/advanced-filter-grid.constants';
import {
  IAdvancedFilterCopyFailure,
  IAdvancedFilterCopyResult,
} from '../../../features/settings-new/models/general/advanced-filter-copy-result.interface';
import {
  formatAdvancedFilterCopyErrorMessage,
  stripUndefinedForFirestore,
} from '../../../features/settings-new/utils/advanced-filter-firestore.utils';
import {
  path_stores_storeId_settings_advanced_filter_groups,
  path_stores_storeId_settings_advanced_filters_meta_migration,
  path_stores_storeId_settings_advanced_filters_meta_sequence,
} from '../database-paths';

@Injectable({
  providedIn: 'root'
})
export class CollectionStoresSettingsService {

  constructor(private firestore: Firestore) {
  }

  // ====================================================================================================
  // Generic Get/Set/Delete
  // ====================================================================================================

  async setDocument<DataType>(
    path: string,
    data: DataType
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data, {merge: true});
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Writes a settings document with merge and rethrows on Firestore errors.
   * Use when callers must know the write succeeded (e.g. paste/copy flows).
   */
  async setDocumentOrThrow<DataType>(
    path: string,
    data: DataType,
  ): Promise<void> {
    const docRef = doc(this.firestore, path);
    await setDoc(docRef, data, {merge: true});
  }

  async getDocument<DataType>(
    path: string
  ): Promise<DataType> {
    try {
      const docRef = doc(this.firestore, path);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return docSnapshot.data() as DataType;
      } else {
        return undefined;
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Reads a settings document. Returns null when the document is missing;
   * rethrows on Firestore errors so callers can distinguish failure from absence.
   */
  async getDocumentOrNull<DataType>(
    path: string,
  ): Promise<DataType | null> {
    const docRef = doc(this.firestore, path);
    const docSnapshot = await getDoc(docRef);
    if (!docSnapshot.exists()) {
      return null;
    }
    return docSnapshot.data() as DataType;
  }

  async deleteDocument<DataType>(
    path: string
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document at path: ${path}\n`, error);
    }
  }

  // ====================================================================================================
  // Advanced Filter Group/s
  // ====================================================================================================

  getStoreSettingsAdvancedFilterGroups(store: IStore): Promise<IAdvancedFilterGroup[]> {
    return new Promise<IAdvancedFilterGroup[]>(async (resolve, reject): Promise<void> => {
      try {
        const collectionRef = collection(this.firestore, path_stores_storeId_settings_advanced_filter_groups(store.storeId));
        const q = query(collectionRef, orderBy('id', 'desc'));
        const querySnapshot: QuerySnapshot = await getDocs(q);

        const advancedFilters: IAdvancedFilterGroup[] = [];
        querySnapshot.forEach((docSnapshot): void => {
          const data = docSnapshot.data() as IAdvancedFilterGroup;
          advancedFilters.push(data);
        });
        resolve(advancedFilters);
      } catch (error) {
        const newError = {
          ...error,
          message: `Error getting stores: Store/Settings/Advanced Filters.\n${error.message}`
        };
        reject(newError);
      }
    });
  }

  /**
   * Copy the selected advanced filter groups from sourceStore into
   * targetStore. Each copied group is given a fresh, non-colliding numeric
   * id derived from the target store's current max id, so existing filters
   * in the target are preserved (merge, not replace). Returns the number
   * of filter groups copied.
   */
  async copyStoreSettingsAdvancedFilterGroups(
    sourceStore: IStore,
    targetStore: IStore,
    filterIds: number[],
  ): Promise<IAdvancedFilterCopyResult> {
    const emptyResult: IAdvancedFilterCopyResult = {copiedCount: 0, skippedCount: 0, failures: []};
    if (!filterIds?.length) {
      return emptyResult;
    }

    const [sourceFilters, targetFilters] = await Promise.all([
      this.getStoreSettingsAdvancedFilterGroups(sourceStore),
      this.getStoreSettingsAdvancedFilterGroups(targetStore),
    ]);

    const idSet = new Set<number>(filterIds);
    const toCopy = sourceFilters.filter((f: IAdvancedFilterGroup): boolean => idSet.has(f.id));
    if (toCopy.length === 0) {
      return emptyResult;
    }

    const existingCrossStoreCopy = new Map<string, IAdvancedFilterGroup>();
    targetFilters.forEach((targetFilter: IAdvancedFilterGroup): void => {
      if (targetFilter.copiedFromStoreId && targetFilter.copiedFromFilterId != null) {
        existingCrossStoreCopy.set(
          `${targetFilter.copiedFromStoreId}:${targetFilter.copiedFromFilterId}`,
          targetFilter,
        );
      }
    });

    const maxExistingId = targetFilters.reduce(
      (max: number, filter: IAdvancedFilterGroup): number => (filter.id > max ? filter.id : max),
      0,
    );

    let nextId = maxExistingId + 1;
    const targetPath = path_stores_storeId_settings_advanced_filter_groups(targetStore.storeId);
    let copiedCount = 0;
    let skippedCount = 0;
    const failures: IAdvancedFilterCopyFailure[] = [];

    for (const filter of toCopy) {
      const sourceKey = `${sourceStore.storeId}:${filter.id}`;
      if (existingCrossStoreCopy.has(sourceKey)) {
        skippedCount++;
        continue;
      }

      const filterName: string = filter.name?.trim() || `Filter ${filter.id}`;
      const newFilter: IAdvancedFilterGroup = {
        ...filter,
        id: nextId,
        saved: true,
        lastModified: new Date(),
        copiedFromStoreId: sourceStore.storeId,
        copiedFromFilterId: filter.id,
      };
      delete newFilter.migratedFromFilterId;
      const payload = stripUndefinedForFirestore(newFilter);
      const docRef = doc(this.firestore, `${targetPath}/${nextId}`);

      try {
        await setDoc(docRef, payload, {merge: true});
        copiedCount++;
        existingCrossStoreCopy.set(sourceKey, newFilter);
        nextId++;
      } catch (error: unknown) {
        failures.push({
          filterId: filter.id,
          filterName,
          message: formatAdvancedFilterCopyErrorMessage(error),
        });
      }
    }

    return {copiedCount, skippedCount, failures};
  }

  /**
   * Duplicate a saved advanced filter into the Stock Take category on the same store.
   * Assigns a fresh numeric id and sets `grid` to stock-take. Nested filter tree ids
   * are preserved (same approach as cross-store copy).
   *
   * The duplicate records the source filter id in `migratedFromFilterId`, and an
   * existing duplicate for the same source is returned as-is. A migration that
   * failed part-way can therefore be retried without creating extra copies.
   */
  async duplicateAdvancedFilterAsStockTake(
    store: IStore,
    sourceFilter: IAdvancedFilterGroup,
  ): Promise<IAdvancedFilterGroup> {
    const {filter} = await this.duplicateAdvancedFilterAsStockTakeWithResult(store, sourceFilter);
    return filter;
  }

  /**
   * Transactional duplicate so concurrent tabs cannot assign the same numeric filter id.
   * Uses per-document reads only (`transaction.get` on doc refs), plus a sequence doc for ids
   * and migration docs for idempotent General → Stock Take copies.
   */
  async duplicateAdvancedFilterAsStockTakeWithResult(
    store: IStore,
    sourceFilter: IAdvancedFilterGroup,
  ): Promise<{ filter: IAdvancedFilterGroup; created: boolean }> {
    await this.ensureStockTakeFilterMigrationMetaInitialized(store);

    const filtersPath = path_stores_storeId_settings_advanced_filter_groups(store.storeId);
    const migrationRef = doc(
      this.firestore,
      path_stores_storeId_settings_advanced_filters_meta_migration(store.storeId, sourceFilter.id),
    );
    const sequenceRef = doc(
      this.firestore,
      path_stores_storeId_settings_advanced_filters_meta_sequence(store.storeId),
    );

    return runTransaction(this.firestore, async (transaction): Promise<{ filter: IAdvancedFilterGroup; created: boolean }> => {
      const migrationSnap = await transaction.get(migrationRef);
      if (migrationSnap.exists()) {
        const stockTakeFilterId = Number(migrationSnap.data()['stockTakeFilterId']);
        if (Number.isFinite(stockTakeFilterId) && stockTakeFilterId > 0) {
          const existingRef = doc(this.firestore, `${filtersPath}/${stockTakeFilterId}`);
          const existingSnap = await transaction.get(existingRef);
          if (existingSnap.exists()) {
            return {filter: existingSnap.data() as IAdvancedFilterGroup, created: false};
          }
        }
      }

      const sequenceSnap = await transaction.get(sequenceRef);
      if (!sequenceSnap.exists()) {
        throw new Error('Stock take advanced filter sequence is not initialized');
      }
      const maxId = Number(sequenceSnap.data()['maxId'] ?? 0);
      const nextId = Number.isFinite(maxId) ? maxId + 1 : 1;
      const newFilter: IAdvancedFilterGroup = stripUndefinedForFirestore({
        ...sourceFilter,
        id: nextId,
        grid: ADVANCED_FILTER_GRID_STOCK_TAKE,
        migratedFromFilterId: sourceFilter.id,
        saved: true,
        lastModified: new Date(),
      });
      const newFilterRef = doc(this.firestore, `${filtersPath}/${nextId}`);
      transaction.set(newFilterRef, newFilter, {merge: true});
      transaction.set(sequenceRef, {maxId: nextId}, {merge: true});
      transaction.set(
        migrationRef,
        {stockTakeFilterId: nextId, generalFilterId: sourceFilter.id},
        {merge: true},
      );
      return {filter: newFilter, created: true};
    });
  }

  /**
   * Seeds the filter id sequence and migration index from existing filter documents (once per store).
   */
  private async ensureStockTakeFilterMigrationMetaInitialized(store: IStore): Promise<void> {
    const sequenceRef = doc(
      this.firestore,
      path_stores_storeId_settings_advanced_filters_meta_sequence(store.storeId),
    );
    const sequenceSnap = await getDoc(sequenceRef);
    if (sequenceSnap.exists()) {
      return;
    }

    const groups = await this.getStoreSettingsAdvancedFilterGroups(store);
    const maxId = groups.reduce(
      (max: number, filter: IAdvancedFilterGroup): number => (filter.id > max ? filter.id : max),
      0,
    );
    await setDoc(sequenceRef, {maxId}, {merge: true});

    const migrationWrites = groups
      .filter(
        (filter: IAdvancedFilterGroup): boolean =>
          filter.grid === ADVANCED_FILTER_GRID_STOCK_TAKE
          && filter.migratedFromFilterId != null
          && Number.isFinite(filter.migratedFromFilterId),
      )
      .map(async (filter: IAdvancedFilterGroup): Promise<void> => {
        const migrationRef = doc(
          this.firestore,
          path_stores_storeId_settings_advanced_filters_meta_migration(store.storeId, filter.migratedFromFilterId),
        );
        const migrationSnap = await getDoc(migrationRef);
        if (!migrationSnap.exists()) {
          await setDoc(
            migrationRef,
            {stockTakeFilterId: filter.id, generalFilterId: filter.migratedFromFilterId},
            {merge: true},
          );
        }
      });
    await Promise.all(migrationWrites);
  }

  /**
   * Copies saved General filters into Stock Take filters on the same store.
   * Skips sources that already have a Stock Take duplicate (`migratedFromFilterId`).
   */
  async copyGeneralAdvancedFiltersToStockTake(
    store: IStore,
    sourceFilters: IAdvancedFilterGroup[],
  ): Promise<IAdvancedFilterCopyResult> {
    let copiedCount = 0;
    let skippedCount = 0;
    const failures: IAdvancedFilterCopyFailure[] = [];
    for (const sourceFilter of sourceFilters) {
      if (!sourceFilter.saved || !isGeneralAdvancedFilterGrid(sourceFilter.grid)) {
        continue;
      }
      const filterName: string = sourceFilter.name?.trim() || `Filter ${sourceFilter.id}`;
      try {
        const {created} = await this.duplicateAdvancedFilterAsStockTakeWithResult(store, sourceFilter);
        if (created) {
          copiedCount++;
        } else {
          skippedCount++;
        }
      } catch (error: unknown) {
        console.error('Failed to copy General filter to Stock Take:', sourceFilter.id, error);
        failures.push({
          filterId: sourceFilter.id,
          filterName,
          message: formatAdvancedFilterCopyErrorMessage(error),
        });
      }
    }
    return {copiedCount, skippedCount, failures};
  }

  async arrayUnionField<T>(path: string, fieldName: string, values: T[]): Promise<void> {
    const docRef = doc(this.firestore, path);

    try {
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        await updateDoc(docRef, {
          [fieldName]: arrayUnion(...values)
        });
      } else {
        await setDoc(docRef, {
          [fieldName]: values
        });
      }
    } catch (error) {
      console.error(`Error performing arrayUnion on field '${fieldName}':`, error);
    }
  }


  async arrayRemoveField<T>(path: string, fieldName: string, values: T[]): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await updateDoc(docRef, {
        [fieldName]: arrayRemove(...values)
      });
    } catch (error) {
      console.error(`Error performing arrayRemove on field '${fieldName}':`, error);
      throw error;
    }
  }


}
